import { Container, Grid, Box, IconButton, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { algoliasearch } from "algoliasearch";
import React, { useMemo, useState, useEffect } from "react";
import {
  Configure,
  ConfigureProps,
  InstantSearch,
  useHits,
  UseHitsProps,
} from "react-instantsearch";
import ProductGalleryHit from "../productGallery/productGalleryHit";

export interface ProductRecommendationsProps {
  sku: string | null;
  warehouseCode: string | null;
  brand: string | null;
  isAuthenticated: boolean;
  productType: string | null;
}

const ProductRecommendations: React.FC<ProductRecommendationsProps> = ({
  sku,
  warehouseCode,
  brand,
  isAuthenticated,
  productType,
}) => {
  const searchClient = useMemo(() => {
    return algoliasearch(
      process.env.REACT_APP_ALGOLIA_ID ?? "",
      process.env.REACT_APP_ALGOLIA_KEY ?? ""
    );
  }, []);

  const CustomHits = (props: UseHitsProps) => {
    const { items } = useHits(props);

    const [loadIndex, setLoadIndex] = useState(0);
    const [currentSlide, setCurrentSlide] = useState(0);

    const handleLoadComplete = (index: number) => {
      if (index === loadIndex) setLoadIndex((prev) => prev + 1);
    };

    const searchOptionalFilters =
      warehouseCode === null || warehouseCode === ""
        ? undefined
        : [
            `sales.lvl1:${warehouseCode}`,
            `sales.lvl2:${warehouseCode}`,
            `sales.lvl3:${warehouseCode}`,
            `sales.lvl4:${warehouseCode}`,
            `sales.lvl5:${warehouseCode}`,
            `sales.lvl6:${warehouseCode}`,
            `sales.lvl7:${warehouseCode}`,
            `sales.lvl8:${warehouseCode}`,
            `sales.lvl9:${warehouseCode}`,
            `sales.lvl10:${warehouseCode}`,
            `sales.lvl11:${warehouseCode}`,
            `sales.lvl12:${warehouseCode}`,
            `sales.lvl13:${warehouseCode}`,
            `sales.lvl14:${warehouseCode}`,
            `sales.lvl15:${warehouseCode}`,
          ];

    const algoliaSku = sku?.replace(/-base$/i, "");
    let searchFilter = `visibleindividually:true AND NOT sku:'${algoliaSku}'`;
    if (warehouseCode) searchFilter += ` AND warehouses:${warehouseCode}`;
    if (brand) searchFilter += ` AND manufacturername:'${brand}'`;
    if (productType !== null && productType !== "") {
      searchFilter += ` AND NOT producttype:${productType}`;
    }

    const getItemsPerSlide = () => {
      if (typeof window === "undefined") return 4;
      const w = window.innerWidth;
      if (w < 600) return 1;
      if (w < 960) return 2;
      return 4;
    };

    const [itemsPerSlide, setItemsPerSlide] = useState<number>(
      getItemsPerSlide()
    );

    useEffect(() => {
      const onResize = () => setItemsPerSlide(getItemsPerSlide());
      window.addEventListener("resize", onResize);
      window.addEventListener("orientationchange", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("orientationchange", onResize);
      };
    }, []);

    const limitedItems = useMemo(() => items.slice(0, 12), [items]);
    const totalSlides = Math.max(
      1,
      Math.ceil(limitedItems.length / itemsPerSlide)
    );

    useEffect(() => {
      if (currentSlide > totalSlides - 1) setCurrentSlide(totalSlides - 1);
    }, [totalSlides]);
    useEffect(() => {
      setCurrentSlide(0);
    }, [itemsPerSlide]);

    const goPrev = () => {
      if (totalSlides < 2) return;
      setCurrentSlide((s) => Math.max(0, s - 1));
    };
    const goNext = () => {
      if (totalSlides < 2) return;
      setCurrentSlide((s) => Math.min(totalSlides - 1, s + 1));
    };

    const isDesktop = itemsPerSlide === 4;
    const isFirst = currentSlide === 0;
    const isLast = currentSlide === totalSlides - 1;

    const showPrev = totalSlides > 1 && (!isDesktop ? true : !isFirst);
    const showNext = totalSlides > 1 && (!isDesktop ? true : !isLast);

    return brand === null ||
      brand === "" ||
      brand !== "Westlake Royal Building Products" ||
      items.length < 4 ? (
      <></>
    ) : (
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <div
          className="product-recommender ais-Hits"
          style={{ background: "white", position: "relative" }}
        >
          <Typography
            component="h2"
            variant="h5"
            sx={{
              mb: "24px",
              color: "#494440",
              fontSize: "20px",
              fontFamily: "Gotham-Medium",
            }}
          >
            {/* Popular from {brand} */}
            Recommended for you
          </Typography>

          <Configure
            {...({
              filters: searchFilter,
              optionalFilters: searchOptionalFilters,
              hitsPerPage: 12,
            } as ConfigureProps)}
          />

          <Box
            sx={{ position: "relative", overflow: "visible", width: "100%" }}
          >
            <Box
              sx={{ position: "relative", overflow: "hidden", width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  transition: "transform 0.4s ease",
                  transform: `translateX(-${currentSlide * 100}%)`,
                }}
              >
                {limitedItems.map((hit: any, index: number) => (
                  <Box
                    key={hit.sku}
                    component="li"
                    sx={{
                      listStyle: "none",
                      flex: "0 0 auto",
                      boxSizing: "border-box",
                      px: 1,
                      width: { xs: "100%", sm: "50%", md: "25%" },
                    }}
                  >
                    <ProductGalleryHit
                      warehouseCode={warehouseCode}
                      hit={hit}
                      position={index}
                      isAuthenticated={isAuthenticated}
                      shouldLoad={index === loadIndex}
                      onLoadComplete={() => handleLoadComplete(index)}
                      hideSkeleton={false}
                      isProductRecommenderTemplate={false}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            {showPrev && (
              <IconButton
                aria-label="Previous"
                onClick={goPrev}
                sx={{
                  color: "#494440",
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  left: { xs: 8, md: -38 },
                  zIndex: 2,
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}

            {showNext && (
              <IconButton
                aria-label="Next"
                onClick={goNext}
                sx={{
                  color: "#494440",
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  right: { xs: 8, md: -38 },
                  zIndex: 2,
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
          </Box>
        </div>
      </Container>
    );
  };

  return (
    <Grid container spacing={0} sx={{ mt: 0 }}>
      <InstantSearch
        searchClient={searchClient}
        indexName={process.env.REACT_APP_ALGOLIA_PRODUCT_INDEX}
        future={{ preserveSharedStateOnUnmount: true }}
      >
        <Grid item xs={12}>
          <Grid container spacing={0}>
            <CustomHits />
          </Grid>
        </Grid>
      </InstantSearch>
    </Grid>
  );
};

export default ProductRecommendations;
